import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get all test groups or paginated test groups
 * Uses POST with body (consistent with projects and testcases)
 * @param {Object} params - Request body parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Number of items per page (default: 25, max: 10000)
 * @param {string} params.query - Text query in canonical format (e.g., "name contains 'API'")
 * @param {string} params.sortField - Field to sort by (default: 'created_at')
 * @param {string} params.sortOrder - Sort direction: 'asc', 'desc', 'ASC', or 'DESC' (default: 'DESC')
 * @returns {Promise<Object>} Response with test groups data
 */
export async function getTestGroups({ page = 1, pageSize = 25, query, filterAst, sortField, sortOrder } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const payload = {
      page,
      pageSize
    };
    
    // Add optional query parameters
    if (query) payload.query = query;
    if (filterAst) payload.filterAst = filterAst;
    if (sortField) payload.sortField = sortField;
    if (sortOrder) payload.sortOrder = sortOrder;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/get`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || { rows: [], total: 0 },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test groups:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get a specific test group by ID
 * @param {Object} params - Parameters
 * @param {string} params.id - Test Group UUID
 * @returns {Promise<Object>} Response with test group data
 */
export async function getTestGroupById({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/get/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      // Handle both formats: { data: { group: {} } } or { data: {} }
      const groupData = response?.data?.data?.group || response?.data?.data || response?.data?.group || {};
      return {
        status: STATUS.SUCCESS,
        data: groupData,
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test group:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new test group
 * @param {Object} params - Test group data
 * @param {string} params.name - Test group name (required)
 * @param {string} params.description - Test group description
 * @param {string} params.next_scheduled_run - Next scheduled run date/time (ISO 8601)
 * @returns {Promise<Object>} Response with created test group
 */
export async function createTestGroup({ name, description, next_scheduled_run } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Test group name is required",
      };
    }

    const payload = { name };
    if (description !== undefined) payload.description = description;
    if (next_scheduled_run !== undefined) payload.next_scheduled_run = next_scheduled_run;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/create`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating test group:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing test group
 * @param {Object} params - Parameters
 * @param {string} params.id - Test Group UUID (required)
 * @param {string} params.name - Test group name
 * @param {string} params.description - Test group description
 * @param {string} params.next_scheduled_run - Next scheduled run date/time (ISO 8601)
 * @returns {Promise<Object>} Response with updated test group
 */
export async function updateTestGroup({ id, name, description, next_scheduled_run } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const payload = {};
    if (name !== undefined) payload.name = name;
    if (description !== undefined) payload.description = description;
    if (next_scheduled_run !== undefined) payload.next_scheduled_run = next_scheduled_run;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/update/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating test group:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete a test group
 * @param {Object} params - Parameters
 * @param {string} params.id - Test Group UUID
 * @returns {Promise<Object>} Response with deletion status
 */
export async function deleteTestGroup({ id } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!id) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/delete/${id}`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting test group:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Bulk delete test groups
 * @param {Object} params - Parameters
 * @param {Array<string>} params.ids - Array of test group IDs to delete
 * @returns {Promise<Object>} Response with deletion count
 */
export async function bulkDeleteTestGroups({ ids } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Test Group IDs array is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/bulk`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || { deletedCount: 0 },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error bulk deleting test groups:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Associate test cases with a test group
 * @param {Object} params - Parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @param {Array<string>} params.testCaseIds - Array of test case IDs
 * @returns {Promise<Object>} Response with association results
 */
export async function associateTestCasesWithTestGroup({ testGroupId, testCaseIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    if (!testCaseIds || !Array.isArray(testCaseIds) || testCaseIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Test Case IDs array is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/associate-testcases`,
      { testGroupId, testCaseIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || { newlyAdded: [], alreadyAssociated: [] },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error associating test cases:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Remove test cases from a test group
 * @param {Object} params - Parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @param {Array<string>} params.testCaseIds - Array of test case IDs to remove
 * @returns {Promise<Object>} Response with removal results
 */
export async function removeTestCasesFromTestGroup({ testGroupId, testCaseIds } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    if (!testCaseIds || !Array.isArray(testCaseIds) || testCaseIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Test Case IDs array is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/remove-testcases/${testGroupId}`,
      {
        data: { testCaseIds },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || { deletedCount: 0, notFound: [] },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error removing test cases:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get available test cases for a test group (not yet associated)
 * @param {Object} params - Query parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @param {string} params.moduleId - Module ID to filter (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Number of items per page (default: 25)
 * @returns {Promise<Object>} Response with available test cases
 */
export async function getAvailableTestCasesForGroup({ testGroupId, moduleId, page = 1, pageSize = 25, search=null, sortField, sortOrder } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const params = { testGroupId, page, pageSize };
    if (moduleId) params.moduleId = moduleId;
    if (search) params.quickFilter = search;
    if (sortField) params.sortField = sortField;
    if (sortOrder) params.sortOrder = sortOrder;
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/available-testcases`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      // Handle both old format { rows, total } and new format { testCases, pagination }
      const responseData = response?.data?.data || response?.data || {};
      const testCases = responseData.testCases || responseData.rows || [];
      const pagination = responseData.pagination || { 
        total: responseData.total || 0, 
        page: responseData.page || 1, 
        pageSize: responseData.pageSize || 25 
      };
      return {
        status: STATUS.SUCCESS,
        data: { testCases, pagination },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching available test cases:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get associated test cases for a test group
 * @param {Object} params - Query parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @param {string} params.moduleId - Module ID to filter (optional)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Number of items per page (default: 25)
 * @returns {Promise<Object>} Response with associated test cases
 */
export async function getAssociatedTestCasesForGroup({ testGroupId, moduleId, page = 1, pageSize = 25, sortField, sortOrder } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const params = { testGroupId, page, pageSize };
    if (moduleId) params.moduleId = moduleId;
    if (sortField) params.sortField = sortField;
    if (sortOrder) params.sortOrder = sortOrder;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/associated-testcases`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      // Handle both old format { rows, total } and new format { testCases, pagination }
      const responseData = response?.data?.data || response?.data || {};
      const testCases = responseData.testCases || responseData.rows || [];
      const pagination = responseData.pagination || { 
        total: responseData.total || 0, 
        page: responseData.page || 1, 
        pageSize: responseData.pageSize || 25 
      };
      return {
        status: STATUS.SUCCESS,
        data: { testCases, pagination },
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching associated test cases:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Fetch modules for test group with counts
 * @param {Object} params - Query parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @returns {Promise<Object>} Response with modules and counts
 */
export async function fetchModulesTestGroupWithCount({ testGroupId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/fetchmodulewithcount`,
      {
        params: { testGroupId },
        withCredentials: true,
      }
    );
    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching modules with count:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get test group summary
 * @param {Object} params - Query parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @returns {Promise<Object>} Response with test group summary
 */
export async function getTestGroupSummary({ testGroupId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/summary`,
      {
        params: { testGroupId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      // Handle both old format (data directly) and new format (data.summary)
      const summaryData = response?.data?.data?.summary || response?.data?.data || response?.data || {};
      return {
        status: STATUS.SUCCESS,
        data: summaryData,
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test group summary:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get test group analytics
 * @param {Object} params - Query parameters
 * @param {string} params.testGroupId - Test Group UUID
 * @returns {Promise<Object>} Response with test group analytics
 */
export async function getTestGroupAnalytics({ testGroupId } = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!testGroupId) {
      return {
        status: STATUS.ERROR,
        error: "Test Group ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/analytics`,
      {
        params: { testGroupId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      // Handle both old format (data directly) and new format (data.analytics)
      const analyticsData = response?.data?.data?.analytics || response?.data?.data || response?.data || {};
      return {
        status: STATUS.SUCCESS,
        data: analyticsData,
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching test group analytics:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Reorder a test case within a test group
 * @param {Object} params
 * @param {string} params.testGroupId - Test group ID
 * @param {string} params.testcaseId - Test case ID to move
 * @param {number} params.newSortOrder - New sort position
 * @returns {Promise<Object>} { status, data?, error? }
 */
export async function reorderTestCaseInGroup({ testGroupId, testcaseId, newSortOrder }) {
  try {
    const projectId = getProjectId();

    if (!projectId) {
      return { status: STATUS.ERROR, error: "Project ID is required" };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/testgroups/reorder/${testGroupId}`,
      { testcaseId, newSortOrder },
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response.data.data,
        message: response.data.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error reordering test case in group:", error);
    return extractErrorInfo(error);
  }
}

